import { WrapResponse } from '@/types';
import { StateSlice } from '@/stores/appStore';
import { snakeToCamel } from '@/lib/utils';
import { fun } from '@/lib/request';
import toast from 'react-hot-toast';
import { api } from '@/lib/env';
import { PaymentType } from "@/stores/slices/commonSlice";
import { paymentTypeToVipType, isPaidVip } from "@/lib/payment";

export type OrderType = 'vip' | 'payment';

export type Order = {
  orderType: OrderType;
  orderNo: string;
  userId: string;
  amount: number;
  status: string;
  createTime?: string;
  payUrl: string;
}

export type PaymentURL = {
  codeUrl: string;
}

export type OpenPaymentParams = {
  open?: boolean;
  type?: PaymentType;
  price?: number;
}

export type PaymentDialogCallback = (params: {
  type: PaymentType;
  status: 'success' | 'cancel';
}) => void;

export interface OrderListData {
  pageNumber: number;
  pageSize: number;
  list: OrderItem[];
  total: number;
}

export interface OrderItem {
  orderType: string;
  orderNo: string;
  amount: number;
  status: string;
  payChannel: string;
  createTime: string;
  payTime: string;
  productName: string;
  desc: string;
  invoiceId: number | null; // 发票ID，null表示未开过票
}

// 发票相关类型定义
export interface InvoiceableOrder {
  orderNo: string;
  desc: string;
  createTime: string;
  invoiceableAmount: number; // 可开票金额（元）
  disabled?: boolean; // 是否禁用（金额为0时禁用）
}

export interface InvoiceRecord {
  id: string;
  applyTime: string; // 申请时间
  amount: number; // 金额（元）
  companyName: string; // 企业抬头
  taxId: string; // 税号
  status: 'processing' | 'completed'; // 开票状态：开票中、已开票
  downloadUrl?: string; // 下载地址（已开票时可用）
}

export interface InvoiceFormData {
  amount: number; // 开票金额
  invoiceType: string; // 发票类型
  invoiceCategory: string; // 发票类目
  companyName: string; // 发票抬头
  taxId: string; // 企业税号
  remarks?: string; // 备注信息
  email: string; // 电子邮箱
}

// 前端KEY和后端互转一下
const PAYMENT_TYPE_TO_ORDER_TYPE: Record<PaymentType, 'vip' | 'payment'> = {
  upgrade: 'vip',
  upgradePlus3x: 'vip',
  recharge: 'payment',
};

const ALL_PAYMENT_TYPES: PaymentType[] = ['upgrade', 'upgradePlus3x', 'recharge'];

const namespace = 'payment';
export type PaymentSlice = {
  [namespace]: {
    // 余额不足提醒，boolean表示是否需要飞豆消耗，数字表示需要消耗的飞豆数量。0表示不需要消耗飞豆，仅弹出提醒
    paymentRecharge: boolean | number;
    openPaymentRecharge: (payment: boolean | number) => void;
    // 是否打开
    paymentOpen: boolean;
    setPaymentOpen: (open: boolean) => void;
    // 打开哪个tab
    paymentType: PaymentType;
    setPaymentType: (type: PaymentType) => void;
    // 价格
    paymentPrice: Record<PaymentType, number>;
    getPaymentPrice: (type?: PaymentType) => number;
    setPaymentPrice: (amount: number, type?: PaymentType) => void;
    // 支付回调
    paymentCallbak: PaymentDialogCallback | null;
    // 清除支付回调
    clearPaymentCallback: () => void;
    // 打开支付
    openPayment: (params: OpenPaymentParams, callback?: PaymentDialogCallback | null) => void;
    paymentSuccessOpenWithTitle: boolean | string;
    setPaymentSuccessOpenWithTitle: (open: boolean | string, callback?: () => void) => void;
    createOrder: (amount?: number) => Promise<Order | null>;
    // 不再轮询，使用 wss
    checkOrder: (orderNo: string) => Promise<Order | null>;
    getPaymentUrl: (orderNo: string, host?: string) => Promise<string>;
    fetchOrderListLoading: boolean;
    fetchOrderList: (params: { page: number; pageSize?: number }) => Promise<OrderListData | null>;
  }
}

type PaymentRequestError = Error & {
  code?: number;
};

export const createPaymentSlice: StateSlice<PaymentSlice> = ((set, _get) => {
  const get = () => _get()[namespace];
  return {
    [namespace]: {
      openPaymentRecharge: (payment) => {
        const paymentOpen = typeof payment === 'boolean' ? payment : payment >= 0;
        set((_state) => {
          const { [namespace]: state } = _state;
          const isVip = _get().userVip?.vip;
          state.paymentOpen = paymentOpen;
          // 1、非会员，打开弹层，默认打开 会员tab
          // 2、会员，买飞豆场景，默认打开 买飞豆 tab，查看会员场景，默认打开 会员tab
          state.paymentType = isPaidVip(isVip) ? 'recharge' : 'upgrade';
          state.paymentRecharge = payment;
          _state.balanceInsufficient = payment;
        });
      },
      paymentOpen: false,
      // 余额不足提醒：
      // boolean 表示是否需要飞豆消耗，
      // 数字表示需要消耗的飞豆数量，0 表示不需要消耗飞豆，仅弹出提醒
      paymentRecharge: 0,
      setPaymentOpen: (open) => {
        set((_state) => {
          const { [namespace]: state } = _state;
          state.paymentOpen = open;
          if (!open) {
            _state.balanceInsufficient = 0;
            state.paymentRecharge = 0;
            state.paymentPrice[state.paymentType] = 0;
            // 注意：不在这里清除 callback，避免 callback 执行延迟
            // callback 的清除由 PaymentDialog 在执行后负责
          }
        });
      },
      paymentType: 'upgrade',
      setPaymentType: (type) => {
        set(({ [namespace]: state }) => {
          state.paymentType = type;
        });
      },
      paymentPrice: {
        upgrade: 0,
        upgradePlus3x: 0,
        recharge: 0,
      },
      getPaymentPrice: (_type?) => {
        const type = _type ?? get().paymentType;
        return get().paymentPrice[type];
      },
      setPaymentPrice: (amount, _type?) => {
        const type = _type ?? get().paymentType;
        set(({ [namespace]: state }) => {
          state.paymentPrice[type] = amount;
        })
      },
      paymentSuccessOpenWithTitle: false,
      setPaymentSuccessOpenWithTitle: (open, callback) => {
        set(({ [namespace]: state }) => {
          state.paymentSuccessOpenWithTitle = open;
        });
        callback?.();
      },
      paymentCallbak: null,
      clearPaymentCallback: () => {
        set(({ [namespace]: state }) => {
          state.paymentCallbak = null;
        });
      },
      openPayment: (params, callback = null) => {
        set(({ [namespace]: state }) => {
          if (typeof params.open === 'boolean') {
            state.paymentOpen = params.open;
          }
          if (params.type && ALL_PAYMENT_TYPES.includes(params.type)) {
            state.paymentType = params.type;
          }
          if (typeof params.price === 'number') {
            state.paymentPrice[params.type || state.paymentType] = params.price;
          }
          // 根据传入的 callback 参数设置，如果打开窗口且有 callback 则设置，否则置空
          // 这样可以确保新的窗口打开时，如果没有传 callback，会清除旧的 callback
          state.paymentCallbak = (state.paymentOpen && callback) ? callback : null;
        });
      },
      createOrder: async (_amount) => {
        const type = get().paymentType;
        const priceNum = typeof _amount === 'number'
          ? _amount
          : get().getPaymentPrice(type);
        if (!priceNum) {
          return null;
        }
        const body: Record<string, string> = {
          amount: priceNum.toString(),
          order_type: PAYMENT_TYPE_TO_ORDER_TYPE[type],
        };
        const vipType = paymentTypeToVipType(type);
        if (vipType) {
          body.vip_type = vipType;
        }
        try {
          const responseData = (await fun.post<WrapResponse<Order>>(
            api('/payment/createOrder', true),
            body,
          )).data;
          if (responseData.code === 0) {
            return snakeToCamel<Order>(responseData.data);
          } else {
            if (responseData.code === 30201) {
              const error: PaymentRequestError = new Error(responseData.message || 'VIP 时间超长');
              error.code = 30201;
              throw error;
            }
            toast.error(responseData.message);
          }
          return null;
        } catch (error) {
          const requestError = error as PaymentRequestError;
          if (requestError?.code === 30201) {
            throw requestError;
          }
          console.error('Error create order:', error);
          return null;
        }
      },
      checkOrder: async (orderNo) => {
        try {
          const formData = new FormData();
          formData.append('order_no', orderNo);
          const responseData = (await fun.post<WrapResponse<Order>>(
            api('/payment/checkOrder', true),
            formData,
          )).data;
          if (responseData.code === 0) {
            const order = snakeToCamel<Order>(responseData.data);
            if (order?.status === 'paid') {
              _get().refreshUserInfo();
            }
            return order;
          }
          return null;
        } catch (error) {
          console.error('Error check order:', error);
          return null;
        } finally {
        }
      },
      getPaymentUrl: async (orderNo) => {
        try {
          const responseData = (await fun.post<WrapResponse<PaymentURL>>(
            api('/payment/prepay', true),
            {
              order_no: orderNo,
            },
          )).data;
          if (responseData.code === 0) {
            return snakeToCamel<PaymentURL>(responseData.data).codeUrl;
          } else {
            toast.error(responseData.message);
          }
          return '';
        } catch (error) {
          console.error('Error getCodeUrl:', error);
          return '';
        } finally {

        }
      },
      fetchOrderListLoading: false,
      fetchOrderList: async ({
        page = 0,
        pageSize,
      }) => {
        try {
          set(({ [namespace]: state }) => {
            state.fetchOrderListLoading = true;
          });
          const responseData = (await fun.post<WrapResponse<OrderListData>>(
            api('/payment/orderList', true),
            {
              page,
              ...(pageSize && { page_size: pageSize }),
            },
          )).data;
          if (responseData.code === 0) {
            return snakeToCamel<OrderListData>(responseData.data);
          }
          return null;
        } catch (error) {
          console.error('Error fetch order:', error);
          return null;
        } finally {
          set(({ [namespace]: state }) => {
            state.fetchOrderListLoading = false;
          });
        }
      },
    },
  };
});
